import styled from "@emotion/styled";
import { Controller, useForm } from "react-hook-form";
import React, { useContext, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Typography, Button, BaseInput, Link } from "@kaltura/ds-react-components";
import { emailRegExString, baseUrl, noop, translate } from "@kaltura/mediaspace-shared-utils";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { FormFieldError } from "@kaltura/mediaspace-shared-ui/form";

export interface VerificationFormProps {
    recaptchaSiteKey?: string;
    recaptchaTheme?: "dark" | "light" | undefined;
    recapthcaMode?: "normal" | "invisible" | undefined;
    processing: boolean;
    errorMessage?: string;
    onSubmit: (data: Record<string, unknown>) => void;
}

const CenteredDiv = styled("div")(({ theme }) => ({
    textAlign: "center",
    marginTop: theme.spacing(3),
}));

const LabelSpan = styled(Typography)(({ theme }) => ({
    fontWeight: theme.kaltura.typography.fontWeightBold,
    color: theme.kaltura.palette.tone1,
    marginTop: theme.spacing(5),
    display: "block",
}));

const LargeButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(5),
    width: "100%",
}));

const StyledEmailInput = styled(
    BaseInput,
    {
        shouldForwardProp: (prop) => prop !== "hasError",
    }
)<{ hasError: boolean }>(({ theme, hasError }) => ({
    width: "100%",
    marginTop: theme.spacing(1),
    ...(
        hasError && {
            /* Acknowledgement/Danger */
            border: `1px solid ${theme.kaltura.palette.danger.main}`,
        }
    ),
}));

const StyledPaddedDiv = styled("div")({
    textAlign: "center",
});

const RecaptchContainer = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(3),
    display: "inline-block",
    ["& .grecaptcha-badge"]: {
        visibility: "hidden",
    },
}));

/**
 * Email Verification Form Component
 */
export function VerificationForm({
    recaptchaSiteKey,
    recaptchaTheme,
    processing,
    errorMessage,
    onSubmit,
    recapthcaMode,
}: VerificationFormProps) {
    const {
        register,
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        criteriaMode: "all",
        reValidateMode: "onChange",
    });

    const config = useContext(ConfigContext);

    const captchaRef = useRef<ReCAPTCHA>(null);

    useEffect(() => {
        setCaptchaToken();
    }, []);

    const setCaptchaToken = async () => {
        if (recapthcaMode === "invisible") {
            const token = await captchaRef.current?.executeAsync();
            setValue("captcha", token);
        }
    };

    const { ref: emailRef, ...emailProps } = register(
        "email",
        {
            required: true,
            pattern: emailRegExString,
        }
    );

    return (
        <form className="siteRegistration-form" onSubmit={handleSubmit(onSubmit)}>
            <div className={"siteRegistration-form__bottom"}>
                <label>
                    <LabelSpan variant={"body2"} className="kms-ds-form-label kms-ds-verification-label">
                        {translate("Enter Email")}
                    </LabelSpan>
                    <div className="form-field">
                        <StyledEmailInput
                            inputRef={emailRef}
                            {...emailProps}
                            type={"email"}
                            autoComplete={"email"}
                            aria-invalid={errors.email || errorMessage ? "true" : "false"}
                            hasError={!!errors.email || !!errorMessage}
                            error={!!errors.email || !!errorMessage}
                        />
                        <div aria-live="polite">
                            {errors.email && (
                                <FormFieldError>
                                    {errors.email.type === "pattern"
                                        ? translate("Please use a valid email address")
                                        : translate("Please enter your email address")}
                                </FormFieldError>
                            )}

                            {errorMessage && <FormFieldError>{HtmlReactParser(errorMessage)}</FormFieldError>}
                        </div>
                    </div>
                </label>

                <StyledPaddedDiv>
                    {recaptchaSiteKey && (
                        <RecaptchContainer className="recaptcha-container">
                            <Controller
                                control={control}
                                rules={{ required: true }}
                                name="captcha"
                                render={({ field: { onChange } }) => (
                                    <ReCAPTCHA
                                        ref={captchaRef}
                                        sitekey={recaptchaSiteKey}
                                        theme={recaptchaTheme}
                                        hl={config.application.currentLocaleCode}
                                        onChange={onChange}
                                        onExpired={setCaptchaToken}
                                        size={recapthcaMode}
                                    />
                                )}
                            />
                            <div aria-live="polite">
                                {errors.captcha && (
                                    <div className={"siteRegistration-error"}>
                                        <span>{translate("Captcha is required")}</span>
                                    </div>
                                )}
                            </div>
                        </RecaptchContainer>
                    )}

                    <LargeButton
                        size={"large"}
                        loading={processing}
                        disabled={processing}
                        className={`siteRegistration__item siteRegistration__submit-button`}
                        onClick={noop}
                        type="submit"
                    >
                        {translate("Send activation email")}
                    </LargeButton>
                </StyledPaddedDiv>

                <CenteredDiv className={"siteRegistration-forgot-password-cancel-link-container"}>
                    <Typography variant={"button"}>
                        <Link
                            className="siteRegistration-forgot-password-cancel__link"
                            href={baseUrl + "/"}
                            size={"small"}
                        >
                            {translate("Cancel")}
                        </Link>
                    </Typography>
                </CenteredDiv>
            </div>
        </form>
    );
}

export default VerificationForm;
